import React from "react";
import { Link } from "react-router-dom";
import "./Landing.css";
import "./Privacy.css";

/** Last substantive change to this policy. */
const LAST_UPDATED = "3 August 2026";
const CONTACT = "hello@thepitchbase.app";

type Props = {};

/**
 * Privacy policy for the Pitchbase product. Content reflects what the platform
 * actually stores (club, squad, fixture and match data, plus uploaded media)
 * and where it runs (Azure UK South, Hostinger).
 */
export class Privacy extends React.Component<Props> {

	componentDidMount() {
		document.title = "Privacy Policy — Pitchbase";
		window.scrollTo(0, 0);
	}

	render() {
		return (
			<div className="lp">
				<header className="lp-nav">
					<div className="lp-nav-inner">
						<Link className="lp-brand" to="/">
							<img alt="Pitchbase" src="/assets/pitchbase-icon.png"/>
							<span>Pitchbase</span>
						</Link>
						<nav className="lp-nav-links">
							<Link to="/">Home</Link>
						</nav>
					</div>
				</header>

				<main className="pv">
					<h1>Privacy Policy</h1>
					<p className="pv-updated">Last updated: {LAST_UPDATED}</p>

					<p>
						Pitchbase is football club management software operated by Debug Labs. This policy
						explains what personal data we handle, why, and what rights you have. It covers the
						Pitchbase web app, mobile app and scoreboard software.
					</p>

					<h2>Who we are</h2>
					<p>
						Debug Labs is the data controller for account data of people who sign in to Pitchbase.
						For the club, squad and match data a club puts into Pitchbase, <strong>the club is the
						data controller and we are a data processor</strong> acting on their instructions.
						Contact us at <a href={`mailto:${CONTACT}`}>{CONTACT}</a>.
					</p>

					<h2>What we collect</h2>
					<h3>Account data</h3>
					<ul>
						<li>Name and email address</li>
						<li>A hashed password (we never store or see your actual password)</li>
						<li>Which club you belong to and your permission groups</li>
						<li>Sign-in activity needed to keep your session secure</li>
					</ul>

					<h3>Club data entered by club staff</h3>
					<ul>
						<li>Club details: name, address, colours, badge, website and social links</li>
						<li>Teams and squads: player names, shirt numbers, positions, photographs and sponsor credits</li>
						<li>Fixtures and match events: scores, goals, cards, substitutions, attendance figures, officials</li>
						<li>Sponsors and partners, and images uploaded for them</li>
						<li>Scoreboard configuration, media uploaded for display, and registered scoreboard devices</li>
					</ul>
					<p className="pv-note">
						Player information — particularly photographs, and any data about players under 18 — is
						personal data. Clubs are responsible for having a lawful basis and appropriate consent
						for anything they upload, and for the accuracy of it.
					</p>

					<h3>Technical data</h3>
					<ul>
						<li>Standard server logs, including IP address, for security and diagnosing faults</li>
						<li>Scoreboard devices report a device identifier and when they last connected</li>
					</ul>

					<h2>Cookies and local storage</h2>
					<p>
						We use browser local storage to hold your sign-in tokens so you stay signed in. These are
						strictly necessary for the service to work. We do not use advertising or third-party
						tracking cookies, and we do not profile you.
					</p>

					<h2>Why we use it</h2>
					<ul>
						<li><strong>To provide the service</strong> — running your club's portal, scoreboard and mobile app (performance of a contract).</li>
						<li><strong>To keep it secure</strong> — authentication, abuse prevention and audit logs (legitimate interests).</li>
						<li><strong>To support you</strong> — responding when you contact us (legitimate interests).</li>
						<li><strong>To meet legal duties</strong> — for example accounting records where a subscription applies.</li>
					</ul>

					<h2>Who we share it with</h2>
					<p>We do not sell personal data. We share it only with providers that run the service on our behalf:</p>
					<ul>
						<li><strong>Microsoft Azure</strong> — application hosting, database and file storage (UK region)</li>
						<li><strong>Hostinger</strong> — hosting for the Pitchbase website</li>
						<li><strong>API-Football (RapidAPI)</strong> — optional squad and fixture lookups when a club uses that feature</li>
					</ul>
					<p>
						We may also disclose data where the law requires it, or to protect our rights or the
						safety of others.
					</p>

					<h2>Where it's stored</h2>
					<p>
						Data is stored on servers in the United Kingdom. Where a provider processes data outside
						the UK, we rely on appropriate safeguards such as the UK International Data Transfer
						Addendum or an adequacy decision.
					</p>

					<h2>How long we keep it</h2>
					<p>
						Club data is kept for as long as the club uses Pitchbase. If a club closes its account we
						delete or anonymise its data within 90 days, unless we must keep records for legal
						reasons. Backups are cycled out within a further 30 days. Clubs can delete individual
						records — players, fixtures, media — at any time from within the app.
					</p>

					<h2>Security</h2>
					<p>
						Access is protected by authentication and role-based permissions, and each club's data is
						separated from every other club's. Passwords are stored using a one-way hash. Traffic is
						encrypted in transit with HTTPS. No system is perfectly secure, but we take these
						measures seriously and review them as the product grows.
					</p>

					<h2>Your rights</h2>
					<p>Under UK GDPR you have the right to:</p>
					<ul>
						<li>Ask what personal data we hold about you and get a copy</li>
						<li>Have inaccurate data corrected</li>
						<li>Ask for data to be deleted, or its use restricted</li>
						<li>Object to processing carried out under legitimate interests</li>
						<li>Ask for your data in a portable format</li>
					</ul>
					<p>
						If the data concerns you as a player or member of a club, please contact that club first,
						as they control it — we will help them respond. Otherwise write to
						<a href={`mailto:${CONTACT}`}> {CONTACT}</a>. You also have the right to complain to the
						Information Commissioner's Office (ico.org.uk).
					</p>

					<h2>Children</h2>
					<p>
						Pitchbase accounts are for club staff and are not intended for children. Clubs may record
						information about youth players; where they do, the club is responsible for obtaining
						consent from a parent or guardian and for deciding what is appropriate to display
						publicly, including on a scoreboard.
					</p>

					<h2>Changes</h2>
					<p>
						If we make significant changes to this policy we will update the date above and, where
						the change materially affects you, tell you in the app or by email.
					</p>

					<p className="pv-contact">
						Questions? <a href={`mailto:${CONTACT}`}>{CONTACT}</a>
					</p>
				</main>

				<footer className="lp-footer">
					<div className="lp-footer-inner">
						<Link className="lp-brand" to="/">
							<img alt="Pitchbase" src="/assets/pitchbase-icon.png"/>
							<span>Pitchbase</span>
						</Link>
						<span>&copy; {new Date().getFullYear()} Pitchbase. All rights reserved.</span>
					</div>
				</footer>
			</div>
		);
	}
}
